import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../apiClient';
import { useAuth } from '../firebase';
import AddCardModal from './AddCardModal';
import { useSubscription } from '../SubscriptionContext';

const PaymentMethods = ({ canUpdatePaymentMethods, onPermissionDenied }) => {
    const { t } = useTranslation('customerInfo');
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [defaultMethod, setDefaultMethod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const currentUser = useAuth();
    const { refetchSubscription, setHasPaymentMethod } = useSubscription();

    const fetchPaymentMethods = useCallback(async () => {
        if (currentUser) {
            try {
                setLoading(true);
                setError('');
                setSuccess('');
                const token = await currentUser.getIdToken();
                const response = await apiClient.get('/v1/payment-methods', { headers: { Authorization: `Bearer ${token}` } });
                
                if (!response.ok) {
                    if (response.status === 404) {
                        setPaymentMethods([]);
                        setDefaultMethod(null);
                        setHasPaymentMethod(false);
                        return;
                    }
                    throw new Error(t('paymentMethodsFetchError'));
                }

                const data = await response.json();
                const methods = data.paymentMethods || [];
                setPaymentMethods(methods);
                setDefaultMethod(data.defaultPaymentMethod);
                setHasPaymentMethod(methods.length > 0);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
    }, [currentUser, t, setHasPaymentMethod]);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const handleSetDefault = async (pmId) => {
        if (!canUpdatePaymentMethods) {
            onPermissionDenied();
            return;
        }
        setError('');
        setSuccess('');
        try {
            const token = await currentUser.getIdToken();
            const response = await apiClient.post(`/v1/payment-methods/${pmId}/set-default`, {}, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error(t('paymentMethodsUpdateError'));
            setSuccess(t('paymentMethodsUpdateSuccess'));
            await fetchPaymentMethods();
            if (refetchSubscription) await refetchSubscription();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCardAdded = async (newPmId) => {
        setHasPaymentMethod(true);
        if (paymentMethods.length === 0 && newPmId) {
            await handleSetDefault(newPmId);
        } else {
            setSuccess(t('paymentMethodsUpdateSuccess'));
            await fetchPaymentMethods();
        }
        if (refetchSubscription) {
            await refetchSubscription();
        }
    };

    const handleDelete = async (pmId) => {
        if (!canUpdatePaymentMethods) {
            onPermissionDenied();
            return;
        }
        if (pmId === defaultMethod) {
            setSuccess('');
            setError(t('paymentMethodsDeleteError'));
            return;
        }
        if (window.confirm(t('paymentMethodsDeleteConfirm'))) {
            setError('');
            setSuccess('');
            try {
                const token = await currentUser.getIdToken();
                const response = await apiClient.delete(`/v1/payment-methods/${pmId}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!response.ok) throw new Error(t('paymentMethodsUpdateError'));
                setSuccess(t('paymentMethodsUpdateSuccess'));
                await fetchPaymentMethods();
                if (refetchSubscription) await refetchSubscription();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    if (loading) {
        return (
            <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
                 <h2 className="text-2xl font-semibold mb-6 text-gray-700">{t('paymentMethodsTitle')}</h2>
                 <p>{t('loading')}</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-700">{t('paymentMethodsTitle')}</h2>

            {error && <p className="text-red-500 mb-4 bg-red-100 p-3 rounded-md">{error}</p>}
            {success && <p className="text-green-500 mb-4 bg-green-100 p-3 rounded-md">{success}</p>}

            <div className="space-y-4">
                {paymentMethods.length === 0 ? (
                    <p className="text-gray-500">{t('paymentMethodsNoMethods')}</p>
                ) : (
                    paymentMethods.map((pm) => (
                        <div key={pm.id} className={`p-4 border rounded-lg flex flex-col sm:flex-row justify-between sm:items-center gap-4 ${pm.id === defaultMethod ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                            <div className='flex-grow'>
                                <span className="font-semibold text-gray-800">{pm.card.brand.toUpperCase()}</span>
                                <span className="ml-4 text-gray-600">**** **** **** {pm.card.last4}</span>
                                <span className="ml-4 text-gray-600">{t('paymentMethodsCardExpires')}: {String(pm.card.exp_month).padStart(2, '0')}/{pm.card.exp_year.toString().slice(-2)}</span>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                                {pm.id === defaultMethod ? (
                                    <span className="font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full text-sm">{t('paymentMethodsDefaultLabel')}</span>
                                ) : (
                                    canUpdatePaymentMethods && <button onClick={() => handleSetDefault(pm.id)} className="font-medium text-indigo-600 hover:text-indigo-800 text-sm">{t('paymentMethodsSetDefaultButton')}</button>
                                )}
                                {pm.id !== defaultMethod && canUpdatePaymentMethods && (
                                    <button onClick={() => handleDelete(pm.id)} className="font-medium text-red-600 hover:text-red-800 text-sm">{t('paymentMethodsDeleteButton')}</button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {canUpdatePaymentMethods && (
                <div className="mt-6">
                    <button onClick={() => setIsModalOpen(true)} className="bg-green-600 text-white font-bold py-2 px-5 rounded-md hover:bg-green-700 transition-colors">
                        {t('paymentMethodsAddCardButton')}
                    </button>
                </div>
            )}
            
            <AddCardModal 
                isOpen={isModalOpen} 
                onRequestClose={() => setIsModalOpen(false)} 
                onCardAdded={handleCardAdded}
                currentUser={currentUser}
            />
        </div>
    );
};

export default PaymentMethods;
